/**
 * Black canvas painting ideas — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_painting_ideas`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const blackCanvasPaintingIdeasPack: ToilePack = {
    slug: 'black-canvas-painting-ideas',
    category: 'craft',
    name: 'Black canvas painting ideas',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'neon', label: 'Neon flowers', subject: 'a black canvas with bright neon pink, orange and lime flowers painted in bold strokes'},
        {key: 'dandelion', label: 'Dandelion', subject: 'a black canvas with a white dandelion blowing its seeds away across the dark'},
        {key: 'lake', label: 'Moonlit lake', subject: 'a black canvas with a full moon over a still lake painted only in white and greys'},
    ],
    styles: [
        {key: 'easel', label: 'On the easel', style: 'standing on a wooden easel against a pale wall, a palette of bright paint on the ledge', palette: 'Pale wall and light wood, so the black canvas and its colours stand out hard.', framing: 'Seen straight on at eye level, the canvas centred.'},
        {key: 'wall', label: 'On the wall', style: 'hung on a light grey living-room wall above a cream sofa arm', palette: 'Soft greys and cream in the room, the black canvas the one dark shape.', framing: 'Seen at eye level from a step back, the canvas centred with wall around it.'},
        {key: 'table', label: 'Work table', style: 'lying flat on a white work table with open pots of paint and a few fine brushes', palette: 'White tabletop and small bright paint pots against the black of the canvas.', framing: 'Seen from directly above, the canvas centred with the supplies at the margins.'},
    ],
    directions: ['galaxy', 'butterflies', 'city skyline'],
    directionHint: 'try: galaxy',
    seo: {
        title: 'Black Canvas Ideas: Neon Flowers, Dandelion, Moonlit Lake',
        metaDescription: 'Nine black canvas ideas: neon flowers, a white dandelion and a moonlit lake in grays, each on an easel, a wall and a work table.',
        h1: 'Black canvas painting ideas: neon flowers, dandelion, moonlit lake',
        tagline: 'Nine finished black canvases, three subjects that use the dark, each shown on an easel, on a wall and on a work table.',
        intro: 'A black canvas makes every color glow, so people paint it for pictures that use the dark rather than cover it. These nine show neon flowers in pink, orange and lime, a white dandelion blowing its seeds across the black, and a full moon over a still lake painted only in whites and grays. Type another subject, such as galaxy or butterflies, and the same dark ground carries it.',
        useCases: [
            'a dark-toned wall piece',
            'a glowing neon-style picture',
            'a black and white room accent',
            'a dramatic gift for a night-sky fan',
        ],
        faq: [
            {q: 'Why paint on black?', a: 'Bright paint looks brighter against black, so the dark ground becomes part of the picture.'},
            {q: 'Are these pictures black and white only?', a: 'No. The neon flowers use bright pink, orange and lime; the dandelion and the lake use white and gray.'},
            {q: 'Can I change the subject?', a: 'Yes. Type another subject and the black ground and the three settings carry it.'},
            {q: 'What are the three settings?', a: 'On an easel against a pale wall, hung in a living room, and lying flat on a white work table with open paint pots.'},
        ],
    },
}
